// LIBRARIES //
import React from 'react';
import { Formik, yupToFormErrors } from 'formik';
import { connect } from 'react-redux';
import Auth from 'j-toker';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';
import moment from 'moment';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import isEmpty from 'lodash/isEmpty';

// COMPONENTS //
import TextBox from 'components/ui/TextBox';
import DatePicker from 'components/ui/DatePicker';
import Select from 'components/ui/SelectField';
import Form from 'components/ui/Form';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import CheckboxNormal from 'components/ui/CheckboxNormal';
import Spacer from 'components/ui/Spacer';
import DebouncedSearch from 'components/ui/DebouncedSearch';
import CtaText from 'components/ui/CtaCard';
import bedImage from 'assets/bed.svg';
import ReactPhoneInput from 'components/shared/ReactPhoneInput';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

// HELPER //
import { toFriendlyDateString, formatPhone } from 'helpers/Formatter';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { isValidEmail } from '../../../helpers/Validator';
import { apiGet, apiPost } from 'helpers/ApiHelper';

const StyledColumn = styled(Col)`
  margin: 8px 0;
`;

const SummaryBox = styled.div`
  border-radius: 16px;
  box-shadow: 0 2px 6px 0 rgba(0, 17, 80, 0.21);
  padding: 16px 24px;
  background: #fff;
`;

const Split = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SummaryHeading = styled.div`
  text-align: center;
  color: #2b2c32;
  font-family: 'Circular-Bold';
  font-size: 20px;
  margin-bottom: 16px;
`;

const FullWidthSection = styled.div`
  width: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  width: 10rem;
  align-items: flex-end;
`;

const HalfWidthSection = styled.div`
  width: 50%;
  ${(props) =>
    props.divider &&
    css`
      border-right: 1px solid #d9d9e3;
    `};
  ${(props) =>
    props.rightAligned &&
    css`
      text-align: right;
    `};
`;

const Place = styled.div`
  color: #65666d;
`;
const Time = styled.div`
  color: #2b2c32;
  font-family: 'Circular-Bold';
`;

const FormTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const FullWidthInput = styled(TextBox)`
  width: 100%;
`;
const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const FullWidthDebouncedSearch = styled(DebouncedSearch)`
  width: 100%;
`;

const FullWidthTextArea = styled(TextBox.TextArea)`
  width: 100%;
`;

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

const StyledCtaText = styled(CtaText)`
  padding: 0px;
`;

const Centered = styled.div`
  text-align: center;
`;

class CreateShowingDrawerDetailsStep extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchActivated: true,
      searchResult: [],
      selectedItem: undefined,
      searchLoading: false,
      birthdayMode: 'year',
    };
  }

  toggleActivateSearch = () => {
    this.setState({
      searchActivated: !this.state.searchActivated,
    });
  };

  handleSearch = (query) => {
    apiGet(`${Auth.getApiUrl()}/landlords/search_prospects_or_residents?query=${query}`)
      .done((response) => {
        this.setState({
          searchResult: response.search_result,
          searchLoading: false,
        });
      })
      .catch((error) => {
        this.setState({ searchLoading: false });
      });
  };

  handleSelectItem = (value, setFieldValue) => {
    const selectedItem = this.state.searchResult.find((item) => item.id === value);
    this.setState({
      selectedItem: selectedItem,
    });
    setFieldValue('lead_id', selectedItem.data.lead_id, true);
    setFieldValue('first_name', selectedItem.data.first_name, true);
    setFieldValue('last_name', selectedItem.data.last_name, true);
    setFieldValue('phone_number', selectedItem.data.phone_number, true);
  };

  clearSelectedItem = (setFieldValue) => {
    this.setState({
      selectedItem: undefined,
    });
    setFieldValue('lead_id', undefined, true);
  };

  handleFormSubmit = (values) => {
    const { selectedItem } = this.state;
    const selectedItemFullName = selectedItem && selectedItem.data && selectedItem.data.full_name;
    const payload = {
      ...values,
      full_name: selectedItemFullName || `${values.first_name} ${values.last_name}`,
      birthday: values.birthday && toFriendlyDateString(values.birthday, { language: 'en' }),
      book_date: values.book_date && toFriendlyDateString(values.book_date, { language: 'en' }),
      move_in_date:
        values.move_in_date && toFriendlyDateString(values.move_in_date, { language: 'en' }),
    };
    this.props.onLoadingChange({ loading: true, error: false });
    apiPost(Auth.getApiUrl() + '/landlords/showings/', payload)
      .done((response) => {
        this.props.onSubmissionComplete(payload);
      })
      .catch((error) => {
        this.props.onLoadingChange({
          formData: payload,
          loading: false,
          error: true,
        });
      });
  };

  validateForm = async (values) => {
    const { searchActivated } = this.state;
    const requiredErrorVerbiage = getTranslatedString('required');
    const invalidEmailVerbiage = getTranslatedString('invalidEmail');
    const invalidPhoneVerbiage = getTranslatedString('validationErrors.invalidPhone');

    let validationSchema = {
      move_in_date: Yup.date().required(requiredErrorVerbiage),
      traffic_source: Yup.string().required(requiredErrorVerbiage),
      max_budget: Yup.string().required(requiredErrorVerbiage),
      notes: Yup.string().required(requiredErrorVerbiage),
      budget_range: Yup.string().required(requiredErrorVerbiage),
    };
    if (searchActivated) {
      validationSchema = {
        ...validationSchema,
        lead_id: Yup.number().required(requiredErrorVerbiage),
      };
    } else {
      validationSchema = {
        ...validationSchema,
        first_name: Yup.string().required(requiredErrorVerbiage),
        last_name: Yup.string().required(requiredErrorVerbiage),
        email: Yup.string().test('email', invalidEmailVerbiage, function (value) {
          return isValidEmail(value);
        }),
        phone_number: Yup.string()
          .required(requiredErrorVerbiage)
          .test('phone_number', invalidPhoneVerbiage, function (value) {
            if (!value) return false;
            if (value.replace(/\s/g, '') === '+00000000000') return true;
            const parseResult = parsePhoneNumberFromString(value);
            return parseResult && parseResult.isValid();
          }),
        birthday: Yup.date(),
      };
    }

    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  handleBirthdayChange = (value, setFieldValue) => {
    this.setState({ birthdayMode: 'year' });
    setFieldValue('birthday', value, true);
  };

  render() {
    const { searchActivated, selectedItem } = this.state;
    const { slotSearchData } = this.props;

    return (
      <React.Fragment>
        <Spacer mb={2}>{this.renderShowingSummary()}</Spacer>
        <Formik
          initialValues={{
            property_id: slotSearchData.property.value,
            desired_unit: slotSearchData.unitType.map((unitType) => unitType.value),
            book_date: slotSearchData.bookDate,
            book_time: slotSearchData.timeSlot[0],
            agent_id: slotSearchData.agentId,
            lead_id: undefined,
            person: undefined,
            first_name: undefined,
            last_name: undefined,
            email: undefined,
            phone_number: undefined,
            birthday: undefined,
            move_in_date: undefined,
            traffic_source: undefined,
            max_budget: undefined,
            min_budget: undefined,
            budget_range: undefined,
            notes: undefined,
            notify_by_sms: true,
            notify_by_email: true,
          }}
          onSubmit={this.handleFormSubmit}
          validate={this.validateForm}
        >
          {({ values, errors, touched, handleChange, setFieldValue, handleSubmit }) => {
            const shouldRenderSearchLead = searchActivated && !selectedItem;
            const shouldRenderSelectedLead = searchActivated && !!selectedItem;
            return (
              <VerticalFormWrapper id={this.props.formName} onSubmit={handleSubmit}>
                {shouldRenderSearchLead && this.renderSearchLead(errors, touched, setFieldValue)}
                {shouldRenderSelectedLead && this.renderSelectedLead(setFieldValue)}
                {!searchActivated &&
                  this.renderCreateLead(values, errors, touched, handleChange, setFieldValue)}
                {this.renderAdditionalInformation(
                  values,
                  errors,
                  touched,
                  handleChange,
                  setFieldValue
                )}
              </VerticalFormWrapper>
            );
          }}
        </Formik>
      </React.Fragment>
    );
  }

  renderShowingSummary = () => {
    const { slotSearchData } = this.props;
    return (
      <SummaryBox>
        <Split>
          <FullWidthSection>
            <Spacer mt={0.6}>
              <Place>{slotSearchData.property.label}</Place>
              <Time>
                {toFriendlyDateString(slotSearchData.bookDate)}, {slotSearchData.timeSlot[0]} -{' '}
                {slotSearchData.timeSlot[1]}
              </Time>
            </Spacer>
          </FullWidthSection>
          <Section>
            <img src={bedImage} alt="Bedroom Icon" />
            <div>
              {slotSearchData.unitType
                .map((unitType) => {
                  if (unitType.translation_key) {
                    return getTranslatedString(`standardUnitTypes.${unitType.translation_key}`);
                  } else {
                    return unitType.label;
                  }
                })
                .join(', ')}
            </div>
          </Section>
        </Split>
      </SummaryBox>
    );
  };

  renderSelectedLead = (setFieldValue) => {
    const record = this.state.selectedItem;
    let heading = null;
    if (record.record_type === 'lead_card') {
      heading = `${record.data.full_name}, ${record.data.property_name}`;
    } else {
      heading = `${record.data.full_name}, ${record.data.property_name}, ${getTranslatedString(
        'showingDrawer.unit'
      )} ${record.data.unit_number}`;
    }
    return (
      <React.Fragment>
        <Spacer mb={1}>
          <SummaryBox>
            <SummaryHeading>{heading}</SummaryHeading>
            <Split>
              <HalfWidthSection divider>
                <div>{formatPhone(record.data.phone_number)}</div>
                <div>{record.data.email}</div>
              </HalfWidthSection>
              <HalfWidthSection rightAligned>
                <div>
                  {record.record_type === 'lead_card'
                    ? getTranslatedString('showingDrawer.prospect')
                    : getTranslatedString('showingDrawer.resident')}
                </div>
              </HalfWidthSection>
            </Split>
          </SummaryBox>
        </Spacer>
        <Spacer mb={2}>
          <Centered>
            <div>{getTranslatedString('showingDrawer.notTheRightPerson')}</div>
            <StyledCtaText onClick={() => this.clearSelectedItem(setFieldValue)}>
              {getTranslatedString('showingDrawer.selectAnotherLeadOrResident')}
            </StyledCtaText>
          </Centered>
        </Spacer>
      </React.Fragment>
    );
  };

  renderSearchLead = (errors, touched, setFieldValue) => {
    return (
      <React.Fragment>
        <Spacer mb={1}>
          <FormTitle>{getTranslatedString('showingDrawer.selectALeadOrResident')}</FormTitle>
        </Spacer>
        <Spacer mb={0.5}>
          <Form.Item
            label={getTranslatedString('showingDrawer.searchByNamePhoneOrEmail')}
            validateStatus={touched.lead_id && errors.lead_id && 'error'}
            help={touched.lead_id && errors.lead_id}
          >
            <FullWidthDebouncedSearch
              showSearch
              placeholder={getTranslatedString('showingDrawer.search')}
              size="large"
              onSearch={this.handleSearch}
              beforeSearch={(query) => this.setState({ searchLoading: true })}
              onChange={(value) => this.handleSelectItem(value, setFieldValue)}
              filterOption={false}
              notFoundContent={null}
              loading={this.state.searchLoading}
            >
              {this.state.searchResult.map((record) => {
                let label = null;
                let desiredUnit = ` - ${record.data.desired_unit_type}`;
                if (record.data.desired_unit_type_translation_code) {
                  desiredUnit = ` - ${getTranslatedString(
                    `standardUnitTypes.${record.data.desired_unit_type_translation_code}`
                  )}`;
                }
                if (record.record_type === 'lead_card') {
                  label = `${record.data.full_name} - ${
                    record.data.property_name
                  }${desiredUnit} • ${getTranslatedString('showingDrawer.prospect')}`;
                } else {
                  label = `${record.data.full_name} - ${
                    record.data.property_name
                  } - ${getTranslatedString('showingDrawer.unit')} ${
                    record.data.unit_number
                  } • ${getTranslatedString('showingDrawer.resident')}`;
                }
                return <Select.Option value={record.id}>{label}</Select.Option>;
              })}
            </FullWidthDebouncedSearch>
          </Form.Item>
        </Spacer>
        <Spacer mb={2}>
          <span>{getTranslatedString('showingDrawer.dontSeeTheLeadYouAreLookingFor')}</span> &nbsp;
          <StyledCtaText onClick={this.toggleActivateSearch}>
            {getTranslatedString('showingDrawer.createNewLead')}
          </StyledCtaText>
        </Spacer>
      </React.Fragment>
    );
  };

  renderCreateLead = (values, errors, touched, handleChange, setFieldValue) => {
    return (
      <React.Fragment>
        <Spacer mb={1}>
          <FormTitle>{getTranslatedString('showingDrawer.createNewLead')}</FormTitle>
        </Spacer>
        <Row gutter={16}>
          <StyledColumn sm={12} xs={24}>
            <Form.Item
              label={`${getTranslatedString('showingDrawer.firstName')}*`}
              validateStatus={touched.first_name && errors.first_name && 'error'}
              help={touched.first_name && errors.first_name}
            >
              <FullWidthInput
                name="first_name"
                value={values.first_name}
                onChange={handleChange}
                placeholder="E.g. James"
                size="large"
              />
            </Form.Item>
          </StyledColumn>
          <StyledColumn sm={12} xs={24}>
            <Form.Item
              label={`${getTranslatedString('showingDrawer.lastName')}*`}
              validateStatus={touched.last_name && errors.last_name && 'error'}
              help={touched.last_name && errors.last_name}
            >
              <FullWidthInput
                name="last_name"
                value={values.last_name}
                onChange={handleChange}
                placeholder="E.g. Ford"
                size="large"
              />
            </Form.Item>
          </StyledColumn>
        </Row>
        <Row gutter={16}>
          <StyledColumn sm={12} xs={24}>
            <Form.Item
              label={getTranslatedString('showingDrawer.email')}
              validateStatus={touched.email && errors.email && 'error'}
              help={touched.email && errors.email}
            >
              <FullWidthInput
                name="email"
                value={values.email}
                onChange={handleChange}
                placeholder="E.g. james@ford.com"
                size="large"
              />
            </Form.Item>
          </StyledColumn>
          <StyledColumn sm={12} xs={24}>
            <Form.Item
              label={`${getTranslatedString('showingDrawer.phoneNumber')}*`}
              validateStatus={touched.phone_number && errors.phone_number && 'error'}
              help={touched.phone_number && errors.phone_number}
            >
              <ReactPhoneInput
                value={values.phone_number}
                onChange={(value) => setFieldValue('phone_number', value)}
                hasError={touched.phone_number && errors.phone_number}
              />
            </Form.Item>
          </StyledColumn>
        </Row>
        <Spacer mb={1}>
          <Row gutter={16}>
            <StyledColumn sm={12} xs={24}>
              <Form.Item
                label={getTranslatedString('showingDrawer.birthday')}
                validateStatus={touched.birthday && errors.birthday && 'error'}
                help={touched.birthday && errors.birthday}
              >
                <FullWidthDatePicker
                  name="birthday"
                  value={values.birthday}
                  defaultPickerValue={moment().locale(getCurrentLocale())}
                  showToday={false}
                  onChange={(value) => this.handleBirthdayChange(value, setFieldValue)}
                  onPanelChange={(value, mode) => {
                    setFieldValue('birthday', undefined); // Have to reset birthday field otherwise, cannot select new month and new year
                    if (mode !== this.state.birthdayMode) {
                      this.setState({ birthdayMode: mode });
                    }
                  }}
                  disabledDate={(current) => current && current > moment().subtract(1, 'day')}
                  maxDate={moment().subtract(16, 'years')}
                  mobileMode="date"
                  mode={this.state.birthdayMode}
                  placeholder="E.g. 1990-01-01"
                  size="large"
                />
              </Form.Item>
            </StyledColumn>
          </Row>
        </Spacer>
        <Spacer mb={2}>
          <Centered>
            <StyledCtaText type="button" onClick={this.toggleActivateSearch}>
              {getTranslatedString('showingDrawer.searchForAnExistingLeadOrResident')}
            </StyledCtaText>
          </Centered>
        </Spacer>
      </React.Fragment>
    );
  };

  renderAdditionalInformation = (values, errors, touched, handleChange, setFieldValue) => {
    const budgetRanges = [
      ...this.props.slotSearchData.property.rent_ranges,
      getTranslatedString('landlordPortal.flexibleBudget'),
    ];

    return (
      <React.Fragment>
        <Spacer mb={1}>
          <FormTitle>{getTranslatedString('showingDrawer.additionalInformation')}</FormTitle>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <StyledColumn sm={12} xs={24}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.moveInDate')}*`}
                validateStatus={touched.move_in_date && errors.move_in_date && 'error'}
                help={touched.move_in_date && errors.move_in_date}
              >
                <FullWidthDatePicker
                  name="move_in_date"
                  defaultPickerValue={moment().locale(getCurrentLocale())}
                  showToday={false}
                  value={values.move_in_date}
                  onChange={(value) => setFieldValue('move_in_date', value, true)}
                  disabledDate={(current) =>
                    current && current < moment().subtract(1, 'day').endOf('day')
                  }
                  minDate={new Date()}
                  mobileMode="date"
                  placeholder="E.g. 2020-01-01"
                  size="large"
                  id="error"
                />
              </Form.Item>
            </StyledColumn>
            <StyledColumn sm={12} xs={24}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.trafficSource')}*`}
                validateStatus={touched.traffic_source && errors.traffic_source && 'error'}
                help={touched.traffic_source && errors.traffic_source}
              >
                <FullWidthSelect
                  name="traffic_source"
                  value={values.traffic_source}
                  onChange={(value) => setFieldValue('traffic_source', value, true)}
                  placeholder={getTranslatedString('showingDrawer.select')}
                  size="large"
                >
                  {!isEmpty(this.props.trafficSources) &&
                    this.props.trafficSources
                      .filter((source) => source.active || source.id === values.traffic_source)
                      .map((source) => (
                        <Select.Option value={source.id}>{source.traffic_source}</Select.Option>
                      ))}
                </FullWidthSelect>
              </Form.Item>
            </StyledColumn>
          </Row>
          <Row gutter={16}>
            <StyledColumn sm={24} xs={24}>
              <Form.Item
                label={`${getTranslatedString('landlordPortal.budgetRange')}*`}
                validateStatus={touched.budget_range && errors.budget_range ? 'error' : null}
                help={touched.budget_range && errors.budget_range}
              >
                <FullWidthSelect
                  name="budget_range"
                  value={values.budget_range}
                  onChange={(value) => {
                    const budgetValues = value.split(' - ');
                    if (budgetValues.length === 2) {
                      setFieldValue('min_budget', budgetValues[0]);
                      setFieldValue('max_budget', budgetValues[1]);
                    } else if (budgetValues.length === 1 && budgetValues[0].includes('$')) {
                      setFieldValue('min_budget', budgetValues[0]);
                      setFieldValue('max_budget', budgetValues[0]);
                    } else {
                      setFieldValue('min_budget', '$0.00');
                      setFieldValue('max_budget', '$0.00');
                    }
                    setFieldValue('budget_range', value, true);
                  }}
                  placeholder={getTranslatedString('showingDrawer.select')}
                  size="large"
                >
                  {budgetRanges.map((option) => (
                    <Select.Option value={option}>{option}</Select.Option>
                  ))}
                </FullWidthSelect>
              </Form.Item>
            </StyledColumn>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Form.Item
            label={`${getTranslatedString('showingDrawer.notes')}*`}
            validateStatus={touched.notes && errors.notes && 'error'}
            help={touched.notes && errors.notes}
          >
            <FullWidthTextArea
              placeholder={getTranslatedString('showingDrawer.addInternalNotes')}
              size="large"
              name="notes"
              value={values.notes}
              onChange={handleChange}
            />
          </Form.Item>
        </Spacer>
        <Spacer>
          <Row gutter={16}>
            <StyledColumn sm={24} xs={24}>
              <Form.Item
                label={`${getTranslatedString(
                  'landlordPortal.showingDetails.howWouldTheProspectPrefer'
                )}*`}
              >
                <Section style={{ alignItems: 'flex-start', width: '100%' }}>
                  <CheckboxNormal
                    name="notify_by_sms"
                    value={values.notify_by_sms}
                    checked={values.notify_by_sms}
                    onChange={handleChange}
                  >
                    {getTranslatedString('landlordPortal.showingDetails.sms')}
                  </CheckboxNormal>
                  <CheckboxNormal
                    name="notify_by_email"
                    value={values.notify_by_email}
                    checked={values.notify_by_email}
                    onChange={handleChange}
                  >
                    {getTranslatedString('landlordPortal.showingDetails.email')}
                  </CheckboxNormal>
                </Section>
              </Form.Item>
            </StyledColumn>
          </Row>
        </Spacer>
      </React.Fragment>
    );
  };
}

export default connect((store) => ({
  properties: store.landlordProperties.properties,
  yardiEnabled: store.userTypes.yardiEnabled,
}))(CreateShowingDrawerDetailsStep);
